import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Leaf, Sun, Egg } from 'lucide-react';

const About = () => {
  const features = [
    {
      icon: <Egg className="w-8 h-8" />,
      title: 'Huevos Frescos',
      description: 'Huevos del día, recién recolectados para tu familia.'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Gallinas Felices',
      description: 'Nuestras gallinas viven en un ambiente saludable y natural.'
    },
    {
      icon: <Leaf className="w-8 h-8" />,
      title: 'Producción Local',
      description: 'Apoyamos la economía local de Copiapó.'
    },
    {
      icon: <Sun className="w-8 h-8" />,
      title: 'Calidad Premium',
      description: 'Productos frescos y de la más alta calidad.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <section
      id="about"
      className="section-container bg-stone-100 relative"
      data-testid="about-section"
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-amber-200/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-300/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="text-amber-600 font-accent text-xl md:text-2xl mb-4">
            Conócenos
          </p>
          <h2
            className="text-4xl md:text-5xl font-semibold text-stone-800 tracking-tight mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="about-title"
          >
            Quiénes Somos
          </h2>
          <p className="text-lg md:text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
            Somos un emprendimiento local ubicado en Copiapó, dedicados a producir huevos frescos del día para tu familia.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          className="grid grid-cols-1 md:grid-cols-12 gap-6"
        >
          {/* Large Image Card */}
          <motion.div
            variants={itemVariants}
            className="md:col-span-7 bento-item image-card bg-white shadow-lg"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1548550023-2bdb3c5beed7?q=80&w=2787&auto=format&fit=crop"
                alt="Gallinas felices"
                className="w-full h-full object-cover"
                data-testid="about-image-hens"
              />
            </div>
            <div className="p-6 md:p-8">
              <h3 className="text-2xl font-semibold text-stone-800 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                Nuestras Gallinas
              </h3>
              <p className="text-stone-600 leading-relaxed">
                Criadas con amor en el norte de Chile, nuestras gallinas disfrutan de un ambiente natural que se refleja en la calidad de cada huevo.
              </p>
            </div>
          </motion.div>

          {/* Stats/Info Card */}
          <motion.div
            variants={itemVariants}
            className="md:col-span-5 bento-item bg-amber-600 text-white p-8 md:p-10 flex flex-col justify-center shadow-lg"
          >
            <p className="font-accent text-2xl text-amber-100 mb-4">Compromiso</p>
            <h3 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Productos Frescos y de Calidad
            </h3>
            <p className="text-amber-100 text-lg leading-relaxed">
              Cada huevo es recolectado diariamente para garantizar la máxima frescura. Directo del campo a tu mesa.
            </p>
          </motion.div>

          {/* Small Image Card */}
          <motion.div
            variants={itemVariants}
            className="md:col-span-5 bento-item image-card bg-white shadow-lg"
          >
            <div className="aspect-[4/3] md:aspect-square overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_farm-happiness/artifacts/m449lrmj_094185a7-55ac-4b21-a84a-2a2c3ff307ae.jpeg"
                alt="Huevos frescos Avícola Santa Adara"
                className="w-full h-full object-contain bg-amber-50"
                data-testid="about-image-eggs"
              />
            </div>
          </motion.div>

          {/* Features Card */}
          <motion.div
            variants={itemVariants}
            className="md:col-span-7 bento-item bg-white p-8 md:p-10 shadow-lg"
          >
            <h3 className="text-2xl font-semibold text-stone-800 mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              ¿Por qué elegirnos?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-4 items-start group"
                  data-testid={`feature-${index}`}
                >
                  <div className="p-3 bg-amber-100 rounded-xl text-amber-600 group-hover:bg-amber-600 group-hover:text-white transition-colors">
                    {feature.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800 mb-1">{feature.title}</h4>
                    <p className="text-stone-600 text-sm">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
